import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// Decode JWT token to get payload
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(
    localStorage.getItem('pt_token') || sessionStorage.getItem('pt_token')
  );
  const [loading, setLoading] = useState(true);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  
  // Track if logout was explicitly called by user action
  const explicitLogoutRef = useRef(false);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user on mount — always restore from storage
  const loadUser = useCallback(async () => {
    const storedToken = localStorage.getItem('pt_token') || sessionStorage.getItem('pt_token');
    
    if (!storedToken) {
      setLoading(false);
      return;
    }

    // Ensure token state is synced with storage
    if (storedToken !== token) {
      setToken(storedToken);
    }
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      const userData = response.data;
      
      // Decode token to get trainer_id and trainee_id
      const tokenData = decodeToken(storedToken);
      if (tokenData) {
        userData.trainer_id = tokenData.trainer_id;
        userData.trainee_id = tokenData.trainee_id;
      }
      
      // Check if user needs password change
      if (userData.first_login_complete === false) {
        setRequiresPasswordChange(true);
      }
      
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Only clear auth on actual auth errors, not network errors
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        localStorage.removeItem('pt_token');
        sessionStorage.removeItem('pt_token');
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Android/iOS back button protection — prevent navigating back to login
  useEffect(() => {
    if (!user || !token) return;

    // Push sentinel entry when user is authenticated
    if (!window.history.state?.sentinel) {
      window.history.pushState({ sentinel: true, authenticated: true }, '');
    }

    const handlePopState = () => {
      const storedToken = localStorage.getItem('pt_token') || sessionStorage.getItem('pt_token');
      if (storedToken && !explicitLogoutRef.current) {
        // Re-push sentinel to block further back navigation past home
        window.history.pushState({ sentinel: true, authenticated: true }, '');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [user, token]);

  // Restore auth on visibility change (app coming to foreground on mobile)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !explicitLogoutRef.current) {
        const storedToken = localStorage.getItem('pt_token') || sessionStorage.getItem('pt_token');
        if (storedToken && !user) {
          loadUser();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, loadUser]);

  const login = async (phone, password, rememberMe = true) => {
    try {
      explicitLogoutRef.current = false;
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        phone,
        password
      });
      
      const { access_token, requires_password_change: needsChange } = response.data;
      
      // Store token based on rememberMe preference
      if (rememberMe) {
        localStorage.setItem('pt_token', access_token);
        sessionStorage.removeItem('pt_token');
      } else {
        sessionStorage.setItem('pt_token', access_token);
        localStorage.removeItem('pt_token');
      }
      
      setToken(access_token);
      
      // Load user data
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      const userResponse = await axios.get(`${API_URL}/api/auth/me`);
      const userData = userResponse.data;
      
      // Decode token to get trainer_id and trainee_id
      const tokenData = decodeToken(access_token);
      if (tokenData) {
        userData.trainer_id = tokenData.trainer_id;
        userData.trainee_id = tokenData.trainee_id;
      }
      
      setUser(userData);
      
      if (needsChange) {
        setRequiresPasswordChange(true);
      }
      
      return { success: true, user: userData, requiresPasswordChange: needsChange };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const forceSetPassword = async (newPassword) => {
    try {
      await axios.post(`${API_URL}/api/auth/force-set-password`, {
        new_password: newPassword
      });
      setRequiresPasswordChange(false);
      // Reload user data
      await loadUser();
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to set password';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    explicitLogoutRef.current = true;
    localStorage.removeItem('pt_token');
    sessionStorage.removeItem('pt_token');
    setToken(null);
    setUser(null);
    setRequiresPasswordChange(false);
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    forceSetPassword,
    requiresPasswordChange,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
